import { Controller } from "@hotwired/stimulus"

// Job Manager (/djobs/:id/manage) — unified Photography + Confirms edit screen.
//
// Drives all the client behavior the Claude Design handoff specifies:
//  - tab switching (swaps the visible panel + updates the page header title,
//    subtitle and the footer Print button)
//  - the collapsible School Information card + its Hide/Show contacts toggle
//  - the panoramic-image file-name display
//  - keeping shared fields (rendered on both tabs) in sync as you type
//  - the Crew & Rigs repeater (Add Rig clones a template row; ✕ removes a row,
//    marking persisted rigs for destruction)
export default class extends Controller {
  static targets = [
    "photoPanel", "confirmsPanel", "photoNav", "confirmsNav",
    "title", "printBtn", "form", "returnTo",
    "schoolBody", "schoolChevron", "schoolToggleLabel", "contactsBtn", "contactsGroup",
    "fileName", "rigsContainer", "rigTemplate", "sync", "weekday",
    "jobNoteValue", "pictureDayValue"
  ]
  static values = { startTab: String }

  connect() {
    this.dirty = false
    this.schoolOpen = true
    // Collapsed by default. The header shows ten fields without it; the rest
    // (district, scode, address, mileage, …) is reference, and leaving it open
    // is what pushed Job Details below the fold. The view server-renders the
    // group with display:none to match, so this only records the state.
    this.showContacts = false
    this.applyContacts()
    this.rigIndex = new Date().getTime()
    // The consolidated screen (djobs#edit_job) has NO tabs -- one panel carries
    // every section, so there is no photoPanel/confirmsPanel to swap and no
    // title to rewrite. Everything else this controller does (rigs, notes,
    // paging, the school panel, dirty tracking) is identical on both screens,
    // so it guards the tab code rather than growing a near-copy of itself.
    if (this.hasPhotoPanelTarget) {
      this.showTab(this.hasStartTabValue && this.startTabValue === "confirms" ? "confirms" : "photography")
    }
  }

  // ---- Tabs ----
  showPhotography() { this.showTab("photography") }
  showConfirms() { this.showTab("confirms") }

  showTab(tab) {
    this.tab = tab
    const isPhoto = tab === "photography"
    this.photoPanelTarget.classList.toggle("is-active", isPhoto)
    this.confirmsPanelTarget.classList.toggle("is-active", !isPhoto)
    this.photoNavTarget.classList.toggle("is-active", isPhoto)
    this.confirmsNavTarget.classList.toggle("is-active", !isPhoto)

    // Title only. The subtitle it used to set went with the element -- a
    // tagline under a heading that already names the tab.
    this.titleTarget.textContent = isPhoto ? "Photography" : "Confirms"
    if (this.hasPrintBtnTarget) this.printBtnTarget.style.display = isPhoto ? "none" : ""
  }

  // The status pill and its refreshPill/updateStatus/confirmsPill helpers are
  // gone. The pill was a read-only echo of Confirm Status that lived outside
  // the form; the field itself now sits in Job Details, where it can be read
  // AND changed in one place. A mirror that only ever restated its source is
  // one more thing to keep in step, not a feature.

  // ---- Paging to the next/previous job ----
  //
  // Previous/Next used to be plain links, so anything typed since the last
  // Update Job was gone the moment you paged. Now they SAVE first: the pending
  // edits go through the ordinary djobs#update, which redirects to whatever
  // `return_to` holds -- so pointing that field at the neighbour turns "save"
  // into "save and go there". No second write path, and the destination still
  // passes the server's safe_return_to guard because it stays a relative path.
  markDirty() {
    this.dirty = true
  }

  goToJob(e) {
    // A modified or middle click means "open in a new tab", which must NOT
    // submit: the form would post from under a page the user is still on and
    // navigate it away. Let the browser have those untouched.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || (e.button !== undefined && e.button !== 0)) return

    const href = e.currentTarget.getAttribute("href")
    if (!href) return
    e.preventDefault()

    // Nothing typed, or nothing to submit through -- just go. Saving here would
    // bump updated_at on every job merely paged past.
    if (!this.dirty || !this.hasFormTarget || !this.hasReturnToTarget) {
      window.location.href = href
      return
    }

    this.returnToTarget.value = href
    // requestSubmit fires submit handlers and native validation the way a real
    // button press does; form.submit() skips both. Guarded for older browsers.
    if (this.formTarget.requestSubmit) {
      this.formTarget.requestSubmit()
    } else {
      this.formTarget.submit()
    }
  }

  // ---- School card ----
  toggleSchool(e) {
    // The Delete Job link sits inside this row. Its click must bubble all the
    // way to document, where jquery_ujs's delegated handler turns data-method
    // into a DELETE + confirm — stopPropagation on the link would silence ujs
    // and the link would navigate as a plain GET. So the link lets the event
    // through and the toggle steps aside instead.
    if (e && e.target.closest(".jm-delete-btn")) return
    this.schoolOpen = !this.schoolOpen
    this.schoolBodyTarget.style.display = this.schoolOpen ? "" : "none"
    this.schoolChevronTarget.innerHTML = this.schoolOpen ? "&#9662;" : "&#9656;"
    this.schoolToggleLabelTarget.textContent = this.schoolOpen ? "Hide details" : "Show details"
    if (this.hasContactsBtnTarget) this.contactsBtnTarget.style.display = this.schoolOpen ? "" : "none"
  }

  toggleContacts(e) {
    if (e) e.stopPropagation()
    this.showContacts = !this.showContacts
    this.applyContacts()
  }

  // Both the initial state and every toggle route through here, so the button
  // label can never drift out of step with what is actually on screen.
  // `contents`, not `block`: the group is a passthrough wrapper whose children
  // are the grid items -- giving it a box of its own would drop all nine
  // fields into a single column.
  applyContacts() {
    if (!this.hasContactsGroupTarget) return
    this.contactsGroupTarget.style.display = this.showContacts ? "contents" : "none"
    if (this.hasContactsBtnTarget) {
      this.contactsBtnTarget.textContent = this.showContacts ? "Hide contacts" : "Contacts"
    }
  }

  // ---- Date weekday (shown next to the Date field) ----
  updateWeekday(e) {
    let label = ""
    const val = e.target.value
    if (val) {
      const d = new Date(val + "T00:00:00")
      if (!isNaN(d.getTime())) label = d.toLocaleDateString("en-US", { weekday: "long" })
    }
    this.weekdayTargets.forEach((el) => { el.value = label })
  }

  // ---- File picker ----
  updateFileName(e) {
    const file = e.target.files && e.target.files[0]
    this.fileNameTarget.textContent = file ? file.name : "No file chosen"
  }

  // ---- Shared-field sync (mirror same-keyed inputs across tabs) ----
  syncField(e) {
    const key = e.target.dataset.syncKey
    if (!key) return
    this.syncTargets.forEach((el) => {
      if (el !== e.target && el.dataset.syncKey === key && el.value !== e.target.value) {
        el.value = e.target.value
      }
    })
  }

  // ---- Structured note template (gray labels + black values) ----
  // Serialize the edited template editor's "Label value" lines into the single
  // hidden field for that note column (job_note / job_note_picture_day).
  rebuildNote(e) {
    const editor = e.target.closest(".jm-note-template")
    if (!editor) return
    const lines = []
    editor.querySelectorAll(".jm-note-row").forEach((row) => {
      const label = row.querySelector(".jm-note-label").textContent.trim()
      const value = row.querySelector(".jm-note-value").value
      lines.push(value ? `${label} ${value}` : label)
    })
    const combined = lines.join("\n")
    const target = e.target.dataset.noteTarget
    if (target === "jobNoteValue" && this.hasJobNoteValueTarget) this.jobNoteValueTarget.value = combined
    if (target === "pictureDayValue" && this.hasPictureDayValueTarget) this.pictureDayValueTarget.value = combined
  }

  // ---- Crew & rigs repeater ----
  // Both mark the form dirty explicitly. They change what the form will POST
  // without any field being typed into, so the delegated input/change listener
  // never fires — and an added or removed rig is exactly the edit you would be
  // most annoyed to lose on the way to the next job.
  addRig() {
    const html = this.rigTemplateTarget.innerHTML.replace(/NEW_RECORD/g, this.rigIndex++)
    this.rigsContainerTarget.insertAdjacentHTML("beforeend", html)
    this.markDirty()
  }

  removeRig(e) {
    const row = e.target.closest('[data-job-manager-target="rigRow"]')
    if (!row) return
    const idField = row.querySelector('input[name*="[id]"]')
    if (idField && idField.value) {
      const destroy = row.querySelector('input[name*="[_destroy]"]')
      if (destroy) destroy.value = "1"
      row.style.display = "none"
    } else {
      row.remove()
    }
    this.markDirty()
  }
}
